import { Form } from 'antd';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { DUBLICATE_CLIENTS_PATH } from '@app/routes/pages/duplicateClientsPages/constants';

import type { DublicateClientsSettingsModel } from '@pages/DublicateClientsSettings/types';

const { useForm } = Form;

const mockSettings: DublicateClientsSettingsModel = {
  SearchByFullName: false,
  SearchByMainPhone: true,
  SearchByEmail: false,
  SearchByInstagram: true,
  SearchByTelegram: true,
  SearchByVk: true,
  BackgroundDuplicatesSearch: false,
  AutoMergeWithoutConflicts: false,
};

const mockFetchSettings = async (): Promise<DublicateClientsSettingsModel> => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return mockSettings;
};

const mockUpdateSettings = async (
  settings: DublicateClientsSettingsModel
): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log('[DublicateClientsSettings] save settings', settings);
  await new Promise((resolve) => setTimeout(resolve, 300));
};

const mockRunDuplicatesSearch = async (): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log('[DublicateClientsSettings] run duplicates search');
  await new Promise((resolve) => setTimeout(resolve, 300));
};

export const useDublicateClientsSettings = () => {
  const navigate = useNavigate();
  const [form] = useForm<DublicateClientsSettingsModel>();
  const { setFieldsValue, validateFields } = form;
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const getDublicateClientsSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await mockFetchSettings();
      setFieldsValue(data);
    } catch (error) {
      showNotification(error, 'warning');
    } finally {
      setIsLoading(false);
    }
  }, [setFieldsValue, showNotification]);

  const saveDublicateClientsSettings = useCallback(async () => {
    try {
      const values = await validateFields();
      setIsSaving(true);
      await mockUpdateSettings(values);
      showNotification('Настройки сохранены', 'success');
      navigate(DUBLICATE_CLIENTS_PATH);
    } catch (error) {
      showNotification(error, 'warning');
    } finally {
      setIsSaving(false);
    }
  }, [validateFields, showNotification, navigate]);

  const saveAndSearchDublicateClientsSettings = useCallback(async () => {
    try {
      const values = await validateFields();
      setIsSaving(true);
      await mockUpdateSettings(values);
      await mockRunDuplicatesSearch();

      showNotification(
        'Настройки сохранены, поиск дубликатов запущен',
        'success'
      );
      navigate(DUBLICATE_CLIENTS_PATH);
    } catch (error) {
      showNotification(error, 'warning');
    } finally {
      setIsSaving(false);
    }
  }, [validateFields, showNotification, navigate]);

  return {
    getDublicateClientsSettings,
    saveDublicateClientsSettings,
    saveAndSearchDublicateClientsSettings,
    isSaving,
    isLoading,
    form,
  };
};
